import { Box, Fade, Flex, Grid, GridItem, Text, useToast } from "@chakra-ui/react";
import { ArrowLeft, ChartDonut, ListBullets } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataTable } from "../../../../components/DataTable";
import LocalSpinner from "../../../../components/LocalSpinner";
import { Page } from "../../../../components/Page";
import { H2, H3 } from "../../../../components/text/headings";
import { routes } from "../../../../config";
import { useGetSuppliersYearlyData } from "../../../../hooks/query";
import { useGetSupplierOverviewData } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import { constructQueryParamsString, getFormattedNumValue, showErrorToast } from "../../../../utils";
import { ScopeCard } from "../components/SummaryCards";
import SupplierComparison from "./SupplierComparison";
import SupplierFiltersCatQualityYear from "./SupplierFiltersCatQualityYear";
import A from "components/A";
import * as React from "react";
import { ToggleButton } from "./SupplierCards";
import { ResponsiveBump } from "@nivo/bump";
import { TrendCalculationMethodDropdown } from "./SupplierTrends";
import { shortenText } from "utils/text-utils";

const SupplierInsightEmissionDetail = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [currentYearData, setCurrentYearData] = useState(null);
  const [previousYearData, setPreviousYearData] = useState(null);
  const [yearByYearEmissionsData, setYearByYearEmissionsData] = useState([]);
  const [filters, setFilters] = useState();
  const [selectedViewType, setSelectedViewType] = useState("table");

  const { data: overviewData, isFetching: isLoading } = useGetSupplierOverviewData(
    [filters?.year, filters?.year - 1],
    filters?.categories,
    filters?.dataQuality,
    null,
    {
      onError: (error) => {
        console.error("Error fetching supplier overview data:", error);
        showErrorToast(toast, "Failed to fetch supplier overview data");
      },
      cacheTime: 60, // 1 minute
    }
  );

  useEffect(() => {
    if (overviewData) {
      setCurrentYearData(overviewData[filters?.year]);
      setPreviousYearData(overviewData[filters?.year - 1]);
    }
  }, [overviewData, filters?.year]);

  const getQueryParamsString = () => {
    let year = filters?.year;
    if (!year) {
      // HACK default - which will also get set when filters is initialised
      year = new Date().getFullYear();
    }
    const currentYear = new Date().getFullYear();
    const yearsSet = new Set([currentYear, year]);
    let yearToAdd = currentYear - 1;
    while (yearsSet.size < 5 && yearToAdd >= 2021) {
      // assuming data is available from 2021 onwards
      yearsSet.add(yearToAdd);
      yearToAdd--;
    }
    const years = Array.from(yearsSet).sort((a, b) => b - a);

    const queryParams = {
      years: years,
      categories: filters?.categories,
      dataQuality: filters?.dataQuality,
      filterYear: year,
    };
    return constructQueryParamsString(queryParams);
  };

  const { isFetching: isSuppliersFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (data) => {
      // exclude data where excludeFromReport is true
      const filteredData = data
        .filter((item) => !item.excludeFromReport)
        .map((item) => {
          const current = item.yearlyEmissionData?.[filters?.year]?.totalCo2Emissions ?? 0;
          const prev = item.yearlyEmissionData?.[filters?.year - 1]?.totalCo2Emissions ?? 0;
          const growth = prev !== 0 ? ((current - prev) / Math.abs(prev)) * 100 : null;
          return { ...item, growth };
        });
      setYearByYearEmissionsData(filteredData);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  return (
    <Page>
      <Flex direction="column" gap={2} mx="10%">
        <Flex onClick={() => navigate(routes.reportingPortalSupplierInsights)} align="center" gap={2} cursor="pointer">
          <ArrowLeft size={22} color="#85C22A" />
          <Text>Value Chain</Text>
        </Flex>

        <H2 fontSize="heading.xl">Supplier Attributable Emissions</H2>

        <Text maxW="75%" color="gray.500" fontSize="body.m">
          Review and analyse the data provided by suppliers
        </Text>

        <Flex direction="column" gap={4}>
          <SupplierFiltersCatQualityYear
            filters={filters}
            setFilters={setFilters}
            showFilters={["year", "quality", "category"]}
          />
        </Flex>

        <Flex direction="column" gap={8} mt={6}>
          <Cards currentYearData={currentYearData} previousYearData={previousYearData} isLoading={isLoading} />
          <H3>Attributable Emissions</H3>

          {/* View Type toggle */}
          <Flex width="fit-content" alignSelf="flex-end" mt={2}>
            <ToggleButton
              options={[
                { label: "Table", value: "table", icon: <ListBullets size={18} /> },
                { label: "Visual", value: "visual", icon: <ChartDonut size={18} /> },
              ]}
              selected={selectedViewType}
              setSelected={setSelectedViewType}
            />
          </Flex>

          <Fade in={selectedViewType === "table"} unmountOnExit>
            <SuppliersTable
              year={filters?.year}
              yearByYearEmissionsData={yearByYearEmissionsData}
              isLoading={isSuppliersFetching}
            />
          </Fade>

          <Fade in={selectedViewType === "visual"} unmountOnExit>
            <SuppliersBumpChart yearByYearEmissionsData={yearByYearEmissionsData} isLoading={isSuppliersFetching} />
          </Fade>

          <SupplierComparison
            year={filters?.year}
            yearByYearEmissionsData={yearByYearEmissionsData}
            isLoading={isSuppliersFetching}
          />
        </Flex>
      </Flex>
    </Page>
  );
};

const Cards = ({ currentYearData, previousYearData, isLoading }) => {
  let delta = currentYearData?.totalTarget
    ? ((currentYearData?.totalEmissions - currentYearData?.totalTarget) / currentYearData?.totalTarget) * 100
    : 0;

  return (
    <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6}>
      <GridItem>
        <ScopeCard
          title="Total"
          theme="dark"
          currentYear={currentYearData?.totalEmissions}
          previousYear={previousYearData?.totalEmissions}
          currentLoading={isLoading}
          previousLoading={isLoading}
        />
      </GridItem>
      <GridItem>
        <ScopeCard
          title="Intensity" /* intensity is on _spend_ (other choices would have been: revenue or units output */
          tooltip="Intensity is the emissions per unit of spend."
          theme="dark"
          unit="tCO2e per £1m"
          currentYear={currentYearData?.spendIntensity}
          previousYear={previousYearData?.spendIntensity}
          currentLoading={isLoading}
          previousLoading={isLoading}
        />
      </GridItem>
      <GridItem>
        <ScopeCard
          title="Tracking" /* "Tracking" means target?! (no idea why) 
		  percentage "change" on this card is not a change but the delta of actual vs target.
		  (there is no way the user can know this - TODO ask design to change this) */
          theme="dark"
          currentYear={currentYearData?.totalTarget}
          previousYear={delta}
          currentLoading={isLoading}
          previousLoading={isLoading}
          calculatePercentageChange={false}
        />
      </GridItem>
    </Grid>
  );
};

/**
 * data comes from getSupplierYearlyData()
 * @param {*} param0
 * @returns
 */
const SuppliersTable = ({ year, yearByYearEmissionsData, isLoading }) => {
  const columns = [
    {
      header: "Supplier",
      accessorKey: "supplierName",
      size: 120,
      enableSorting: true,
      sortingFn: "alphanumeric", // Use a predefined sorting function
      cell: ({ cell, row }) => (
        <A
          color="lime.500"
          textDecoration="underline"
          // _hover={{ textDecoration: "underline" }}
          href={`${routes.supplierInsightsIndividualDetail}?year=${year}&supplierId=${row?.original?.supplierId}`}
        >
          {cell.getValue()}
        </A>
      ),
    },
    {
      header: "Scope 1",
      accessorKey: `yearlyEmissionData.${year}.scope1`,
      size: 120,
      cell: ({ cell }) => getFormattedNumValue(cell.getValue(), 2),
      enableSorting: true,
      sortingFn: "alphanumeric",
      skipNumericCheck: true,
    },
    {
      header: "Scope 2",
      accessorKey: `yearlyEmissionData.${year}.scope2`,
      size: 120,
      cell: ({ cell }) => getFormattedNumValue(cell.getValue(), 2),
      enableSorting: true,
      sortingFn: "alphanumeric",
      skipNumericCheck: true,
    },
    {
      header: "Scope 3",
      accessorKey: `yearlyEmissionData.${year}.scope3`,
      size: 120,
      enableSorting: true,
      cell: ({ cell }) => getFormattedNumValue(cell.getValue(), 2),
      sortingFn: "alphanumeric",
      skipNumericCheck: true,
    },
    {
      header: "Total",
      accessorKey: `yearlyEmissionData.${year}.totalCo2Emissions`,
      size: 120,
      enableSorting: true,
      cell: ({ cell }) => getFormattedNumValue(cell.getValue(), 2),
      sortingFn: "alphanumeric",
      skipNumericCheck: true,
    },
    {
      header: "Growth (%)",
      accessorKey: "growth",
      size: 100,
      cell: ({ cell }) => getFormattedNumValue(cell.getValue(), 2),
      enableSorting: true,
      sortingFn: "alphanumeric",
      skipNumericCheck: true,
    },
    {
      header: "Intensity",
      accessorKey: `yearlyEmissionData.${year}.intensity`,
      size: 120,
      enableSorting: true,
      cell: ({ cell }) => getFormattedNumValue(cell.getValue(), 2),
      sortingFn: "alphanumeric",
      skipNumericCheck: true,
    },
  ];

  return (
    <>
      {isLoading ? (
        <LocalSpinner />
      ) : (
        <DataTable
          data={yearByYearEmissionsData}
          columns={columns}
          isLoading={isLoading}
          serverSidePagination={false}
          enableSorting={true}
          enableFilters={true}
          fontSize="body.xs"
          queryPageSize={10}
        />
      )}
    </>
  );
};

const SuppliersBumpChart = ({ yearByYearEmissionsData, isLoading }) => {
  const [method, setMethod] = useState("Emission");

  if (isLoading) {
    return <LocalSpinner />;
  }

  function convertEmissionDataV1(inputData) {
    return inputData?.map((supplier) => {
      // Extract yearly emission data
      const yearlyData = supplier?.yearlyEmissionData;

      // Convert each year's data to the required format
      const dataPoints = Object.entries(yearlyData)?.map(([year, emissions]) => ({
        x: year,
        y: emissions?.totalCo2Emissions !== 0 ? Number(emissions?.totalCo2Emissions) : null,
      }));

      return {
        id: supplier?.supplierName,
        data: dataPoints,
      };
    });
  }

  /**
   * Converts supplier emission data to format required by Nivo ResponsiveBump chart.
   * Inverts emission values so higher emissions appear higher on the chart (lower rank).
   *
   * @param {Array} inputData - Array of supplier objects with yearlyEmissionData
   * @returns {Array} Array of series objects for Nivo bump chart
   */
  function convertEmissionData(inputData, method = "Emission") {
    if (!inputData || inputData.length === 0) return [];

    // Collect all unique years across all suppliers
    const years = new Set();
    inputData.forEach((supplier) => {
      Object.keys(supplier?.yearlyEmissionData || {}).forEach((year) => years.add(year));
    });

    const yearList = Array.from(years).sort();

    // Determine which data field to use based on method
    const dataField = method === "Intensity" ? "intensity" : "totalCo2Emissions";

    // Calculate global maximum value once for inversion
    const maxValue = Math.max(
      ...inputData.flatMap((supplier) =>
        Object.values(supplier?.yearlyEmissionData || {}).map((data) => data?.[dataField] || 0)
      )
    );

    return inputData.map((supplier) => ({
      id: supplier?.supplierName,
      data: yearList.map((year) => {
        const value = supplier?.yearlyEmissionData?.[year]?.[dataField] || 0;

        // Invert value so higher values = higher y position on chart
        const invertedValue = value > 0 ? maxValue - value : null;

        return {
          x: year,
          y: invertedValue,
          value: value, // Store original value for tooltip display
        };
      }),
    }));
  }

  const barHeight = 40;
  const minHeight = 500;
  const chartHeight = Math.max(minHeight, yearByYearEmissionsData?.length * barHeight);

  return (
    <Box>
      <TrendCalculationMethodDropdown setSelected={setMethod} selected={method} />
      <Box height={`${chartHeight}px`} width="100%" mt={4}>
        <ResponsiveBump
          data={convertEmissionData(yearByYearEmissionsData, method)}
          margin={{ top: 20, right: 20, bottom: 40, left: 150 }}
          startLabel={(serie) => shortenText(serie.id, 20, 15)}
          startLabelTextColor={{ from: "serie.color" }}
          endLabel={false}
          colors={{ scheme: "spectral" }}
          lineWidth={3}
          activeLineWidth={6}
          inactiveLineWidth={3}
          inactiveOpacity={0.15}
          pointSize={10}
          activePointSize={16}
          inactivePointSize={0}
          pointColor={{ theme: "background" }}
          pointBorderWidth={3}
          activePointBorderWidth={3}
          pointBorderColor={{ from: "serie.color" }}
          // axisRight={{
          //   legend: 'Ranking',
          //   legendOffset: 80,
          //   tickPadding: 5,
          // }}
          axisLeft={null}
          useMesh={true}
          pointTooltip={({ point }) => (
            <Box bg="white" p={3} borderRadius="md" boxShadow="lg" border="1px solid" borderColor="gray.200">
              <Text fontWeight="bold" color="gray.800">
                {point.serie.id}
              </Text>
              <Text color="gray.600" fontSize="sm">
                Year: {point.data.x}
              </Text>
              <Text color="gray.600" fontSize="sm">
                {method === "Intensity" ? "Intensity" : "Emissions"}: {getFormattedNumValue(point.data.value, 2, true)}{" "}
                {method === "Intensity" ? "" : "tCO₂e"}
              </Text>
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

export default SupplierInsightEmissionDetail;
